import { Component, inject, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { AppSidebar } from './app.sidebar';
import { AppFooter } from './app.footer';
import { LayoutService } from '../service/layout.service';
import { FileService, KeycloakService } from '@shared/services/api';
import { ExtendedMenuItem } from '@shared/models';
import { PortalAccessCheckService } from '@shared/services/utils';
import { environment as env } from '@env/environment';

@Component({
	selector: 'app-layout',
	standalone: true,
	imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, AppFooter],
	template: `<div class="layout-wrapper" [ngClass]="containerClass">
		<app-sidebar [logo]="logo" [sub$]="sub$"></app-sidebar>
		<div class="layout-right">
			<app-topbar [switchOptions]="switchOptions"></app-topbar>

			<div class="layout-main-container">
				<div class="layout-main">
					<router-outlet></router-outlet>
				</div>
				<app-footer></app-footer>
			</div>
		</div>
		<div class="layout-mask animate-fadein"></div>
	</div> `
})
export class AppLayout implements OnDestroy {
	overlayMenuOpenSubscription: Subscription;

	menuOutsideClickListener: any;
	sub$!: Subscription;
	logo: string | null = null;
	switchOptions: ExtendedMenuItem[] = [];

	@ViewChild(AppSidebar) appSidebar!: AppSidebar;

	@ViewChild(AppTopbar) appTopBar!: AppTopbar;

	authService = inject(KeycloakService);
	fs = inject(FileService);
	layoutService = inject(LayoutService);
	renderer = inject(Renderer2);
	router = inject(Router);
	accessCheckService = inject(PortalAccessCheckService);

	constructor() {
		this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
			if (!this.menuOutsideClickListener) {
				this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
					if (this.isOutsideClicked(event)) {
						this.hideMenu();
					}
				});
			}

			if (this.layoutService.layoutState().staticMenuMobileActive) {
				this.blockBodyScroll();
			}
		});

		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
			this.hideMenu();
		});

		if (this.authService.currentUser()?.company?.logoUrl) {
			const url = this.authService.currentUser()?.company?.logoUrl ?? '';
			this.sub$ = this.fs.getFile(url).subscribe((blob) => {
				this.logo = URL.createObjectURL(blob);
			});
		}
	}

	openRecruitmentPortal() {
		const recruitmentBaseUrl = env.recruitmentUrl;
		window.open(recruitmentBaseUrl, '_self');
	}

	isOutsideClicked(event: MouseEvent) {
		const sidebarEl = document.querySelector('.layout-sidebar');
		const topbarEl = document.querySelector('.layout-menu-button');
		const eventTarget = event.target as Node;

		return !(
			sidebarEl?.isSameNode(eventTarget) ||
			sidebarEl?.contains(eventTarget) ||
			topbarEl?.isSameNode(eventTarget) ||
			topbarEl?.contains(eventTarget)
		);
	}

	hideMenu() {
		this.layoutService.layoutState.update((prev) => ({
			...prev,
			overlayMenuActive: false,
			staticMenuMobileActive: false,
			menuHoverActive: false
		}));
		if (this.menuOutsideClickListener) {
			this.menuOutsideClickListener();
			this.menuOutsideClickListener = null;
		}
		this.unblockBodyScroll();
	}

	blockBodyScroll(): void {
		if (document.body.classList) {
			document.body.classList.add('blocked-scroll');
		} else {
			document.body.className += ' blocked-scroll';
		}
	}

	unblockBodyScroll(): void {
		if (document.body.classList) {
			document.body.classList.remove('blocked-scroll');
		} else {
			document.body.className = document.body.className.replaceAll(
				new RegExp(String.raw`(^|\b)${'blocked-scroll'.split(' ').join('|')}(\b|$)`, 'gi'),
				' '
			);
		}
	}

	get containerClass() {
		return {
			'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
			'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
			'layout-static-inactive':
				this.layoutService.layoutState().staticMenuDesktopInactive &&
				this.layoutService.layoutConfig().menuMode === 'static',
			'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
			'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive
		};
	}

	ngOnDestroy() {
		if (this.overlayMenuOpenSubscription) {
			this.overlayMenuOpenSubscription.unsubscribe();
		}

		if (this.menuOutsideClickListener) {
			this.menuOutsideClickListener();
		}
	}
}
